import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Switch,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconBrandAndroid,
  IconBrandApple,
  IconCalendarCheck,
  IconClock,
  IconDeviceMobileOff,
  IconPencil,
  IconRefresh,
  IconServer,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { successNotification } from "@/shared/utils/successNotification";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import OrderSyncPasswordModal from "@/pages/admin/orders/OrderSyncPasswordModal";
import { ORDER_SYNC_PASSWORD } from "@/shared/constants/general.const";

// ─── Types ────────────────────────────────────────────────────────────────────

type AppVersionField = "minIosVersion" | "latestIosVersion" | "minAndroidVersion" | "latestAndroidVersion";

type FieldState = {
  value: string;
  editValue: string;
  editing: boolean;
  saving: boolean;
};

// Tracks which action is awaiting password confirmation
type PendingAction =
  | { type: "versionField"; field: AppVersionField }
  | { type: "maintenanceToggle"; value: boolean };

// ─── Cron job display config ───────────────────────────────────────────────────

const CRON_JOBS = [
  {
    key: "navSyncLastRunAt" as const,
    title: "NAV Sync",
    description: "Syncs latest Net Asset Value for all mutual fund schemes from AdvisorKhoj.",
    schedule: "Daily at 08:00 AM & 01:00 PM IST",
  },
  {
    key: "portfolioStatsLastCalculatedAt" as const,
    title: "Portfolio Stats",
    description: "Computes platform-wide total invested, current value, and XIRR.",
    schedule: "Daily at 09:00 PM IST",
  },
  {
    key: "orderSyncLastRunAt" as const,
    title: "Order Sync",
    description: "Reconciles active order statuses against BSE for the last 14 days.",
    schedule: "Daily at 12:00 AM IST",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(value: string | null): string {
  if (!value) return "Never";
  return new Date(value).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  });
}

function fieldLabel(field: AppVersionField): string {
  switch (field) {
    case "minIosVersion": return "iOS Min Version";
    case "latestIosVersion": return "iOS Latest Version";
    case "minAndroidVersion": return "Android Min Version";
    case "latestAndroidVersion": return "Android Latest Version";
  }
}

// ─── CronCard ─────────────────────────────────────────────────────────────────

function CronCard({
  title,
  description,
  schedule,
  lastRunAt,
}: {
  title: string;
  description: string;
  schedule: string;
  lastRunAt: string | null;
}) {
  const hasRun = !!lastRunAt;
  return (
    <Paper radius="sm" bg="#F9F8F8" p="md" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <Group gap={8}>
        <ThemeIcon variant="light" color={hasRun ? "green" : "gray"} size="sm" radius="xl">
          <IconRefresh size={12} />
        </ThemeIcon>
        <Text fw={600} fz="sm">{title}</Text>
      </Group>

      <Text fz="xs" c="slate.5" style={{ lineHeight: 1.5 }}>{description}</Text>

      <Divider />

      <Group gap={6}>
        <IconClock size={13} color="var(--mantine-color-slate-5)" />
        <Text fz="xs" c="slate.5">Schedule:</Text>
        <Text fz="xs" fw={500}>{schedule}</Text>
      </Group>

      <Group gap={6}>
        <IconCalendarCheck size={13} color={hasRun ? "var(--mantine-color-green-6)" : "var(--mantine-color-slate-4)"} />
        <Text fz="xs" c="slate.5">Last run:</Text>
        <Text fz="xs" fw={500} c={hasRun ? "dark" : "slate.4"}>{formatDateTime(lastRunAt)}</Text>
      </Group>
    </Paper>
  );
}

// ─── VersionRow — shows min + latest for one platform in a single row ─────────

function VersionRow({
  minField,
  latestField,
  fields,
  anyEditing,
  onEdit,
  onChange,
  onSave,
}: {
  minField: AppVersionField;
  latestField: AppVersionField;
  fields: Record<AppVersionField, FieldState>;
  anyEditing: boolean;
  onEdit: (field: AppVersionField) => void;
  onChange: (field: AppVersionField, value: string) => void;
  onSave: (field: AppVersionField) => void;
}) {
  const renderInput = (field: AppVersionField, label: string) => {
    const state = fields[field];
    const disabled = anyEditing && !state.editing;
    return (
      <Box style={{ flex: 1 }}>
        <TextInput
          label={label}
          value={state.editing ? state.editValue : state.value}
          onChange={(e) => onChange(field, e.currentTarget.value)}
          readOnly={!state.editing}
          disabled={disabled}
          placeholder="—"
          radius={10}
          styles={{
            input: {
              backgroundColor: "#FFFFFF",
              borderColor: "#BFBFBF",
              cursor: state.editing ? "text" : "default",
            },
          }}
        />
        <Group justify="flex-end" mt={6}>
          {state.editing ? (
            <Button size="xs" color="primary" onClick={() => onSave(field)} loading={state.saving}>
              Save
            </Button>
          ) : (
            <Button
              size="xs"
              variant="outline"
              color="primary"
              leftSection={<IconPencil size={12} />}
              onClick={() => onEdit(field)}
              disabled={disabled}
            >
              Edit
            </Button>
          )}
        </Group>
      </Box>
    );
  };

  return (
    <Group align="flex-start" gap="md" grow>
      {renderInput(minField, "Minimum Supported Version")}
      {renderInput(latestField, "Latest Version")}
    </Group>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ServerConfigPage() {
  const [loading, setLoading] = useState(true);
  const [cronData, setCronData] = useState<Record<typeof CRON_JOBS[number]["key"], string | null>>({
    navSyncLastRunAt: null,
    portfolioStatsLastCalculatedAt: null,
    orderSyncLastRunAt: null,
  });
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);

  const initField = (): FieldState => ({ value: "", editValue: "", editing: false, saving: false });
  const [fields, setFields] = useState<Record<AppVersionField, FieldState>>({
    minIosVersion: initField(),
    latestIosVersion: initField(),
    minAndroidVersion: initField(),
    latestAndroidVersion: initField(),
  });

  // Tracks the action waiting for password confirmation
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await trpc.maintenance.getServerConfig.query();
        const d = res.data;

        setCronData({
          navSyncLastRunAt: d.navSyncLastRunAt ? String(d.navSyncLastRunAt) : null,
          orderSyncLastRunAt: d.orderSyncLastRunAt ? String(d.orderSyncLastRunAt) : null,
          portfolioStatsLastCalculatedAt: d.portfolioStatsLastCalculatedAt ? String(d.portfolioStatsLastCalculatedAt) : null,
        });

        setMaintenanceEnabled(d.mobileMaintenanceEnabled);

        setFields({
          minIosVersion: { ...initField(), value: d.minIosVersion ?? "", editValue: d.minIosVersion ?? "" },
          latestIosVersion: { ...initField(), value: d.latestIosVersion ?? "", editValue: d.latestIosVersion ?? "" },
          minAndroidVersion: { ...initField(), value: d.minAndroidVersion ?? "", editValue: d.minAndroidVersion ?? "" },
          latestAndroidVersion: { ...initField(), value: d.latestAndroidVersion ?? "", editValue: d.latestAndroidVersion ?? "" },
        });
      } catch (err) {
        handleTrpcError(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const anyEditing = Object.values(fields).some((f) => f.editing);

  // ── Version field handlers ────────────────────────────────────────────────────

  const handleEdit = (field: AppVersionField) => {
    setFields((prev) => ({
      ...prev,
      [field]: { ...prev[field], editing: true, editValue: prev[field].value },
    }));
  };

  const handleChange = (field: AppVersionField, value: string) => {
    setFields((prev) => ({ ...prev, [field]: { ...prev[field], editValue: value } }));
  };

  // Clicking Save opens the password modal instead of saving directly
  const handleSaveRequest = (field: AppVersionField) => {
    setPendingAction({ type: "versionField", field });
    setPasswordModalOpen(true);
  };

  // ── Maintenance toggle handler ────────────────────────────────────────────────

  const handleMaintenanceToggleRequest = (value: boolean) => {
    setPendingAction({ type: "maintenanceToggle", value });
    setPasswordModalOpen(true);
  };

  // ── Password confirmed — execute the pending action ───────────────────────────

  const handleAuthorized = async () => {
    if (!pendingAction) return;

    if (pendingAction.type === "versionField") {
      const { field } = pendingAction;
      const { editValue } = fields[field];
      setFields((prev) => ({ ...prev, [field]: { ...prev[field], saving: true } }));
      try {
        await trpc.maintenance.updateAppVersion.mutate({ field, value: editValue });
        setFields((prev) => ({
          ...prev,
          [field]: { ...prev[field], value: editValue, editing: false, saving: false },
        }));
        successNotification({ title: "Updated", message: `${fieldLabel(field)} updated successfully.` });
      } catch (err) {
        handleTrpcError(err);
        setFields((prev) => ({ ...prev, [field]: { ...prev[field], saving: false } }));
      }
    }

    if (pendingAction.type === "maintenanceToggle") {
      const { value } = pendingAction;
      setMaintenanceSaving(true);
      try {
        await trpc.maintenance.setMobileMaintenanceMode.mutate({ enabled: value });
        setMaintenanceEnabled(value);
        successNotification({
          title: "Updated",
          message: `Mobile maintenance mode ${value ? "enabled" : "disabled"}.`,
        });
      } catch (err) {
        handleTrpcError(err);
      } finally {
        setMaintenanceSaving(false);
      }
    }

    setPendingAction(null);
  };

  const handleModalClose = () => {
    // Cancel: if a version field was pending, exit its editing state
    if (pendingAction?.type === "versionField") {
      const { field } = pendingAction;
      setFields((prev) => ({ ...prev, [field]: { ...prev[field], saving: false } }));
    }
    setPendingAction(null);
    setPasswordModalOpen(false);
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Loader size="lg" type="dots" />
      </Center>
    );
  }

  const passwordModalMeta = pendingAction
    ? pendingAction.type === "maintenanceToggle"
      ? {
          title: `${pendingAction.value ? "Enable" : "Disable"} Mobile Maintenance Mode`,
          description: `This will ${pendingAction.value ? "put the mobile app into maintenance mode, blocking all user access" : "restore normal mobile app access"}. Enter the admin action password to continue.`,
        }
      : {
          title: `Update ${fieldLabel(pendingAction.field)}`,
          description: `You are about to update the ${fieldLabel(pendingAction.field)}. Enter the admin action password to confirm.`,
        }
    : { title: "", description: "" };

  return (
    <Flex direction="column" w="100%" p="xl" style={{ overflowY: "auto", height: "100vh" }}>
      <Group gap={8} mb="xl">
        <IconServer size={22} />
        <Title order={2} fw={700}>Server Configuration</Title>
      </Group>

      {/* ── Scheduled Jobs ── */}
      <Text fw={600} fz="md" mb="sm">Scheduled Jobs</Text>
      <SimpleGrid cols={3} spacing="lg" mb="xl">
        {CRON_JOBS.map((job) => (
          <CronCard
            key={job.key}
            title={job.title}
            description={job.description}
            schedule={job.schedule}
            lastRunAt={cronData[job.key]}
          />
        ))}
      </SimpleGrid>

      <Divider mb="xl" />

      {/* ── Mobile App Maintenance ── */}
      <Text fw={600} fz="md" mb="sm">Mobile App</Text>
      <Paper radius="sm" bg="#F9F8F8" p="md" mb="xl">
        <Group justify="space-between" align="center">
          <Group gap={10}>
            <ThemeIcon variant="light" color={maintenanceEnabled ? "orange" : "gray"} size="md" radius="xl">
              <IconDeviceMobileOff size={14} />
            </ThemeIcon>
            <Box>
              <Text fw={600} fz="sm">Maintenance Mode</Text>
              <Text fz="xs" c="slate.5">
                When enabled, the mobile app will show a maintenance screen to all users.
              </Text>
            </Box>
          </Group>
          <Switch
            checked={maintenanceEnabled}
            onChange={(e) => handleMaintenanceToggleRequest(e.currentTarget.checked)}
            disabled={maintenanceSaving}
            color="orange"
            size="md"
          />
        </Group>
      </Paper>

      <Divider mb="xl" />

      {/* ── App Versions ── */}
      <Text fw={600} fz="md" mb="sm">App Versions</Text>
      <SimpleGrid cols={2} spacing="lg">
        {/* iOS */}
        <Paper radius="sm" bg="#F9F8F8" p="md">
          <Group gap={8} mb="md">
            <ThemeIcon variant="light" color="dark" size="sm" radius="xl">
              <IconBrandApple size={12} />
            </ThemeIcon>
            <Text fw={600} fz="sm">iOS</Text>
          </Group>
          <VersionRow
            minField="minIosVersion"
            latestField="latestIosVersion"
            fields={fields}
            anyEditing={anyEditing}
            onEdit={handleEdit}
            onChange={handleChange}
            onSave={handleSaveRequest}
          />
        </Paper>

        {/* Android */}
        <Paper radius="sm" bg="#F9F8F8" p="md">
          <Group gap={8} mb="md">
            <ThemeIcon variant="light" color="green" size="sm" radius="xl">
              <IconBrandAndroid size={12} />
            </ThemeIcon>
            <Text fw={600} fz="sm">Android</Text>
          </Group>
          <VersionRow
            minField="minAndroidVersion"
            latestField="latestAndroidVersion"
            fields={fields}
            anyEditing={anyEditing}
            onEdit={handleEdit}
            onChange={handleChange}
            onSave={handleSaveRequest}
          />
        </Paper>
      </SimpleGrid>

      {/* ── Password confirmation modal ── */}
      <OrderSyncPasswordModal
        opened={passwordModalOpen}
        onClose={handleModalClose}
        onAuthorized={handleAuthorized}
        expectedPassword={ORDER_SYNC_PASSWORD}
        title={passwordModalMeta.title}
        description={passwordModalMeta.description}
      />
    </Flex>
  );
}
